/**
 * Содержимое кейса.
 *
 * Шансы показываются честно и до открытия: это и есть главный аргумент
 * доверия к кейсам, а полоска под каждым предметом даёт понять соотношение
 * быстрее, чем проценты.
 */

import { formatPaws, type CaseItemDto } from "@koshkodrop/shared";

import { RARITY_COLOR, RARITY_LABEL, RARITY_ORDER, rarityTileStyle } from "./rarity";

export function CaseContents({ items }: { items: CaseItemDto[] }) {
  const sorted = [...items].sort(
    (a, b) => RARITY_ORDER.indexOf(b.rarity) - RARITY_ORDER.indexOf(a.rarity),
  );
  const maxChance = Math.max(...items.map((item) => item.chanceBp), 1);

  return (
    <section className="space-y-2">
      <header className="flex items-baseline justify-between px-1">
        <h3 className="text-xs font-extrabold uppercase tracking-wide text-white/40">
          Что внутри
        </h3>
        <span className="text-[10px] font-bold text-white/30">шансы честные</span>
      </header>

      <div className="grid grid-cols-2 gap-2">
        {sorted.map((item) => {
          const color = RARITY_COLOR[item.rarity];

          return (
            <article
              key={item.id}
              className="overflow-hidden rounded-2xl border p-2.5"
              style={rarityTileStyle(item.rarity)}
            >
              <div className="flex items-center gap-2.5">
                <span
                  className="flex size-10 shrink-0 items-center justify-center rounded-xl text-xl"
                  style={{ background: `color-mix(in oklab, ${color} 22%, transparent)` }}
                >
                  {item.emoji}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-[12px] font-extrabold text-white">{item.name}</p>
                  <p className="tabular text-[11px] font-bold" style={{ color }}>
                    {formatPaws(BigInt(item.value))} 🐾
                  </p>
                </div>
              </div>

              <div className="mt-2.5 flex items-center gap-2">
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/8">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(4, (item.chanceBp / maxChance) * 100)}%`,
                      background: color,
                    }}
                  />
                </div>
                <span className="tabular text-[10px] font-bold text-white/45">
                  {formatChance(item.chanceBp)}
                </span>
              </div>

              <p className="mt-1 text-[9px] font-bold uppercase tracking-wide text-white/25">
                {RARITY_LABEL[item.rarity]}
              </p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/** Редкие предметы имеют доли процента: округление до целых показало бы 0%. */
function formatChance(chanceBp: number): string {
  const percent = chanceBp / 100;
  if (percent >= 10) return `${percent.toFixed(0)}%`;
  if (percent >= 1) return `${percent.toFixed(1)}%`;
  return `${percent.toFixed(2)}%`;
}
